// A Tree Change's preview (docs/cockpit/spec.md): what changes in plain words, the open Runs it affects, and
// what replaying the Tenant's recent Runs on it found (ADR-0014), with Confirm for an Owner and the choice to
// move open Runs to the new version. A change the replay found would leave Runs with no route, or fail a
// statutory check, says why and offers no Confirm. Runs not tested because the agent's instructions (its
// Playbook) changed can be tested with the agent, at the Credits shown, or skipped: skipping leaves them untested
// and Confirm stays. Settings and web chat both show it; confirming and testing go to the Application API, which
// checks again that the person is an Owner.
import { useState } from "react";
import type { AppliedTreeChange, TreeChange } from "../api.js";
import { CATALOGUES, useWords, type Words } from "../i18n/words.js";

/** What confirming did, in plain words. */
export function appliedNotice(applied: AppliedTreeChange, words: Words = CATALOGUES.en): string {
  const moved = applied.migrated.length;
  const stayed = applied.pinned.length;
  return [words.changeApplied, ...(moved > 0 || stayed > 0 ? [words.changeMoved(moved, stayed)] : [])].join(" ");
}

/** Thousandths of a Credit as Credits, to one decimal place at most: 1500 is "1.5". */
const credits = (milliCredits: number) => String(Math.round(milliCredits / 100) / 10);

/** A Tree Change's preview, with Confirm for an Owner: in Settings and as a card in web chat. */
export function ChangePreview({
  change,
  owner,
  busy,
  onConfirm,
  onDismiss,
  onTestWithAgent,
  refusal,
}: {
  change: TreeChange;
  owner: boolean;
  busy: boolean;
  onConfirm: (change: TreeChange, migrate: boolean) => void;
  onDismiss?: () => void;
  /** The Owner agrees to spend the Credits shown on testing the change with the agent. */
  onTestWithAgent?: (change: TreeChange, milliCredits: number) => void;
  /** Why confirming it was refused, said on the card; a change drafted before another one says to say it again. */
  refusal?: string | undefined;
}) {
  const w = useWords();
  const [migrate, setMigrate] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const migratable = change.preview.impact.reduce((n, i) => n + i.migratable.length, 0);
  const { replay } = change.preview;
  const blocking = replay?.blocking ?? [];
  const offer = owner && change.status !== "applied" && onTestWithAgent ? replay?.agentTest : undefined;
  return (
    <section className="card" aria-label={w.changePreviewLabel}>
      <p>
        <strong>{change.preview.headline}</strong>
      </p>
      <ul className="plain-list">
        {change.preview.lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {change.preview.impact.length > 0 ? <p className="small muted">{w.changeKeepVersion}</p> : null}
      {replay ? (
        <div aria-label={w.changeReplayLabel}>
          <p>{replay.headline}</p>
          {replay.examples.length > 0 ? (
            <ul className="plain-list small">
              {replay.examples.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {offer && !skipped ? (
        <div aria-label={w.changeAgentTestLabel}>
          <p>{w.changeAgentTestOffer(offer.runs)}</p>
          <p>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => onTestWithAgent?.(change, offer.milliCredits)}
            >
              {w.changeAgentTest(credits(offer.milliCredits))}
            </button>{" "}
            <button type="button" className="btn" disabled={busy} onClick={() => setSkipped(true)}>
              {w.changeAgentSkip}
            </button>
          </p>
        </div>
      ) : offer ? (
        <p className="small muted">{w.changeAgentSkipped}</p>
      ) : null}
      {replay?.agentTested && replay.agentTested.length > 0 ? (
        <div aria-label={w.changeAgentTestedLabel}>
          <ul className="plain-list small">
            {replay.agentTested.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {blocking.length > 0 && change.status !== "applied" ? (
        <div role="alert">
          <p>
            <strong>{w.changeBlocked}</strong>
          </p>
          <ul className="plain-list">
            {blocking.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {onDismiss ? (
            <p>
              <button type="button" className="btn" onClick={onDismiss}>
                {w.changeNotNow}
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
      {refusal !== undefined && change.status !== "applied" ? <p role="alert">{refusal}</p> : null}
      {owner && change.status !== "applied" && blocking.length === 0 && refusal === undefined ? (
        <>
          {migratable > 0 ? (
            <p>
              <label>
                <input type="checkbox" checked={migrate} onChange={(e) => setMigrate(e.target.checked)} />{" "}
                {w.changeAlsoMove(migratable)}
              </label>
            </p>
          ) : null}
          <p>
            <button type="button" className="btn btn-go" disabled={busy} onClick={() => onConfirm(change, migrate)}>
              {w.changeConfirm}
            </button>{" "}
            {onDismiss ? (
              <button type="button" className="btn" onClick={onDismiss}>
                {w.changeNotNow}
              </button>
            ) : null}
          </p>
        </>
      ) : change.status === "applied" ? (
        <p role="status">{w.changeAppliedStatus}</p>
      ) : blocking.length > 0 ? null : (
        <p role="status">{w.changeSentToOwner}</p>
      )}
    </section>
  );
}
